import type { ServerEventName, ServerEventPayload } from '@lagerfeuer/shared';

type Listener<E extends ServerEventName> = (payload: ServerEventPayload<E>) => void;

const listeners = new Map<ServerEventName, Set<Listener<never>>>();

/**
 * Abonnieren einzelner Gateway-Ereignisse (für Andockstellen Extras/Voice und für Seiteneffekte).
 * Gibt eine Abmelde-Funktion zurück.
 */
export function onGatewayEvent<E extends ServerEventName>(event: E, fn: Listener<E>): () => void {
  let set = listeners.get(event);
  if (!set) listeners.set(event, (set = new Set()));
  set.add(fn as Listener<never>);
  return () => set.delete(fn as Listener<never>);
}

export function emitGatewayEvent<E extends ServerEventName>(event: E, payload: ServerEventPayload<E>): void {
  for (const fn of listeners.get(event) ?? []) {
    try {
      (fn as Listener<E>)(payload);
    } catch (e) {
      console.error(`Listener für ${event} fehlgeschlagen`, e);
    }
  }
}

// ---------- Neu-Synchronisation nach Wiederverbindung ----------

const resyncListeners = new Set<() => void>();

/**
 * Wird nach einem erneuten READY (Socket-Neuverbindung bzw. READY nach REST-Ersatz) aufgerufen: geladene
 * Ausschnitte nachladen, abgelaufene Abfragen erneuern. Gibt eine Abmelde-Funktion zurück.
 */
export function onResync(fn: () => void): () => void {
  resyncListeners.add(fn);
  return () => resyncListeners.delete(fn);
}

export function emitResync(): void {
  for (const fn of [...resyncListeners]) {
    try {
      fn();
    } catch (e) {
      console.error('Resync-Listener fehlgeschlagen', e);
    }
  }
}

/** Kanäle mit gerade angezeigter Nachrichtenliste (Referenzzählung; Panel + Hauptbereich möglich). */
const openChannels = new Map<string, number>();

export function markChannelOpen(channelId: string): () => void {
  openChannels.set(channelId, (openChannels.get(channelId) ?? 0) + 1);
  return () => {
    const n = (openChannels.get(channelId) ?? 1) - 1;
    if (n <= 0) openChannels.delete(channelId);
    else openChannels.set(channelId, n);
  };
}

export function isChannelOpen(channelId: string): boolean {
  return openChannels.has(channelId);
}
